import { ApiService } from '@/shared/services/api.service';
import { Logger } from '@/shared/services/logger.service';

/**
 * Server-side persistence for the Noise Suppression device setting.
 *
 * The toggle used to live only in localStorage, so clearing browser storage (or using another
 * browser/machine) silently reset it to OFF. It is now stored against the user's `meeting` app
 * preferences, with localStorage kept purely as a fast local cache so the toggle can render
 * without waiting on the network.
 */
const MEETING_APP_SLUG = 'meeting';
const NOISE_SUPPRESSION_SETTING_KEY = 'noiseSuppression';
const PREFERENCE_ENDPOINT = `user-org-preferences/app-settings/${MEETING_APP_SLUG}`;

/**
 * Cached server value for this page session. `undefined` = not fetched yet, `null` = fetched but
 * the user has never set the preference (callers should keep their current/default value).
 */
let cachedPreference: boolean | null | undefined;
let inFlightFetch: Promise<boolean | null> | null = null;

/**
 * Reads the stored Noise Suppression preference for the current user.
 *
 * @returns `true`/`false` when the user has set the preference, or `null` when they never have
 *   (or the request failed) so the caller can fall back to its own default.
 */
export async function fetchNoiseSuppressionPreference(): Promise<boolean | null> {
  if (cachedPreference !== undefined) {
    return cachedPreference;
  }
  if (inFlightFetch) {
    return inFlightFetch;
  }

  inFlightFetch = (async () => {
    try {
      const response = await ApiService.get<any>(PREFERENCE_ENDPOINT);
      const stored = response?.settings?.[NOISE_SUPPRESSION_SETTING_KEY];
      cachedPreference = typeof stored === 'boolean' ? stored : null;
      return cachedPreference;
    } catch (error) {
      Logger.warn('[NoiseSuppression] Failed to load preference from server', error);
      // Deliberately not cached: a transient failure must not pin the value to null for the
      // rest of the session.
      return null;
    } finally {
      inFlightFetch = null;
    }
  })();

  return inFlightFetch;
}

/**
 * Persists the Noise Suppression preference for the current user.
 *
 * Uses PATCH so the `meeting` settings blob is merged server-side — a PUT here would drop the
 * other meeting preferences (toolbar visibility, floating panel, ...) written by other screens.
 *
 * @param enabled - The state to store.
 */
export async function persistNoiseSuppressionPreference(enabled: boolean): Promise<void> {
  cachedPreference = enabled;
  try {
    await ApiService.patch(PREFERENCE_ENDPOINT, {
      settings: { [NOISE_SUPPRESSION_SETTING_KEY]: enabled },
    });
  } catch (error) {
    Logger.error('[NoiseSuppression] Failed to persist preference to server', error);
  }
}

/** Drops the cached value — call on logout so the next user does not inherit it. */
export function clearNoiseSuppressionPreferenceCache(): void {
  cachedPreference = undefined;
  inFlightFetch = null;
}
